import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/ApiConfig';
import DefaultModal from './DefaultModal';
import useDefaultModal from '../hooks/useDefaultModal';

function ItemEditModal({ show, onClose, onConfirm, item, dfdTipo }) {
  const [formData, setFormData] = useState({
    quantidade: '',
    unidade: '',
    descricaoComplementar: '',
  });
  const [unidadesMedida, setUnidadesMedida] = useState([]);
  const [isLoadingUnidades, setIsLoadingUnidades] = useState(false);
  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const token = localStorage.getItem('token');

  // Carrega unidades de medida quando o modal abre
  useEffect(() => {
    if (show && item) {
      // Reseta o formulário, se o item tiver campo 'um', usa como valor padrão
      setFormData({
        quantidade: '',
        unidade: item.um || '', // Define UM do TABSUS como padrão se existir
        descricaoComplementar: '',
      });

      // Carrega unidades de medida
      loadUnidadesMedida();
    }
  }, [show, item]);

  const loadUnidadesMedida = async () => {
    setIsLoadingUnidades(true);
    try {
      let url = '';
      let unidades = [];
      
      // Se o item possui campo 'um' (vindo do TABSUS), carrega apenas essa unidade
      if (item.um) {
        console.log('Item do TABSUS detectado, carregando apenas unidade:', item.um);
        setUnidadesMedida([{ descricao: item.um }]);
        setIsLoadingUnidades(false);
        return;
      }
      
      if (dfdTipo === 'MATERIAL') {
        // Busca unidades do CATMAT_UM filtradas por codigoPdm no backend
        const pdmNumerico = parseInt(item.pdm, 10);
        url = `${API_BASE_URL.CATMAT_UM}/by-pdm?codigoPdm=${pdmNumerico}`;
      } else if (dfdTipo === 'SERVIÇO') {
        // Busca unidades do CATSER_UM filtradas por codigoServico no backend
        const codigoNumerico = parseInt(item.codigo, 10);
        url = `${API_BASE_URL.CATSER_UM}/by-servico?codigoServico=${codigoNumerico}`;
      }

      if (url) {
        console.log('Buscando unidades de medida filtradas:', url);
        
        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });

        unidades = response.data || [];
        console.log('Unidades encontradas:', unidades.length);
        
        // Se não encontrou unidades nas APIs específicas, busca na API genérica
        if (unidades.length === 0) {
          console.log('Lista vazia, buscando em unidade_medida...');
          const fallbackResponse = await axios.get(`${API_BASE_URL.UNIDADE_MEDIDA}/all`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          
          unidades = fallbackResponse.data || [];
          console.log('Unidades encontradas em unidade_medida:', unidades.length);
        }
        
        setUnidadesMedida(unidades);
        // Define unidade padrão se ainda não houver valor no formulário
        if (unidades.length > 0) {
          const first = unidades[0];
          let sigla;
          if (first.siglaUnidadeFornecimento) sigla = first.siglaUnidadeFornecimento;
          else if (first.siglaUnidadeMedida) sigla = first.siglaUnidadeMedida;
          else sigla = first.descricao;
          setFormData(prev => ({ ...prev, unidade: prev.unidade || sigla }));
        }
      }
    } catch (error) {
      console.error('Erro ao carregar unidades de medida:', error);
      console.error('Detalhes do erro:', error.response?.data);
      
      // Em caso de erro, tenta buscar da API genérica como fallback
      try {
        console.log('Tentando fallback para unidade_medida devido a erro...');
        const fallbackResponse = await axios.get(`${API_BASE_URL.UNIDADE_MEDIDA}/all`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const fallbackUnits = fallbackResponse.data || [];
        setUnidadesMedida(fallbackUnits);
        console.log('Unidades carregadas do fallback:', fallbackUnits.length || 0);
        if (fallbackUnits.length > 0) {
          const first = fallbackUnits[0];
          const sigla = first.descricao;
          setFormData(prev => ({ ...prev, unidade: prev.unidade || sigla }));
        }
      } catch (fallbackError) {
        console.error('Erro ao carregar fallback de unidade_medida:', fallbackError);
        setUnidadesMedida([]);
      }
    } finally {
      setIsLoadingUnidades(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = () => {
    // Valida quantidade
    if (!formData.quantidade || parseFloat(formData.quantidade) <= 0) {
      showDefaultModal('error', 'Por favor, informe uma quantidade válida!');
      return;
    }

    // Valida unidade de medida
    if (!formData.unidade) {
      showDefaultModal('error', 'Por favor, selecione uma unidade de medida!');
      return;
    }

    onConfirm(formData);
  };

  if (!show) return null;

  return (
    <>
      <div 
        className="modal fade show d-block" 
        tabIndex="-1" 
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 1050 }}
      >
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header text-white py-1" style={{ background: 'linear-gradient(90deg, #0b3383, #0e48ad)' }}>
            <h6 className="modal-title text-white m-0 small">
              <i className="bi bi-pencil-square me-2"></i>
              Editar item antes de adicionar
            </h6>
            <button
              className="btn btn-link text-white p-0 mb-0"
              onClick={onClose}
              title="Fechar"
              style={{ fontSize: '0.9rem', textDecoration: 'none' }}
            >
              <i className="bi bi-x-lg"></i>
            </button>
          </div>
          
          <div className="modal-body">
            {/* Informações do item */}
            <div className="mb-3 p-3 bg-light rounded">
              <h6 className="text-primary mb-2">
                <i className="bi bi-info-circle me-2"></i>
                Informações do Item
              </h6>
              <div className="row">
                <div className="col-md-12">
                  <p className="mb-1">
                    <strong>Código:</strong> {item?.codigo || `${item?.grupo}-${item?.classe}-${item?.pdm}`}
                  </p>
                  <p className="mb-0">
                    <strong>Descrição:</strong> {item?.descricao}
                  </p>
                </div>
              </div>
            </div>

            {/* Formulário */}
            <div className="row">
              <div className="col-md-6 mb-3">
                <label htmlFor="quantidade" className="form-label">
                  <i className="bi bi-hash me-1"></i>
                  Quantidade <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  className="form-control"
                  id="quantidade"
                  name="quantidade"
                  value={formData.quantidade}
                  onChange={handleChange}
                  placeholder="Digite a quantidade"
                  min="0"
                  step="0.01"
                  required
                />
              </div>

              <div className="col-md-6 mb-3">
                <label htmlFor="unidade" className="form-label">
                  <i className="bi bi-rulers me-1"></i>
                  Unidade de Medida <span className="text-danger">*</span>
                </label>
                {isLoadingUnidades ? (
                  <select className="form-select" disabled>
                    <option>Carregando unidades...</option>
                  </select>
                ) : unidadesMedida.length > 0 ? (
                  <select
                    className="form-select"
                    id="unidade"
                    name="unidade"
                    value={formData.unidade}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Selecione...</option>
                    {unidadesMedida.map((um, index) => {
                      // CATMAT_UM usa siglaUnidadeFornecimento/nomeUnidadeFornecimento
                      // CATSER_UM usa siglaUnidadeMedida/nomeUnidadeMedida
                      // UnidadeMedida usa descricao/descricaoDetalhada
                      let sigla, nome;
                      
                      if (um.siglaUnidadeFornecimento) {
                        // Dados de CATMAT_UM
                        sigla = um.siglaUnidadeFornecimento;
                        nome = um.nomeUnidadeFornecimento;
                      } else if (um.siglaUnidadeMedida) {
                        // Dados de CATSER_UM
                        sigla = um.siglaUnidadeMedida;
                        nome = um.nomeUnidadeMedida;
                      } else {
                        // Dados de UnidadeMedida (fallback)
                        sigla = um.descricao;
                        nome = um.descricaoDetalhada;
                      }
                      
                      return (
                        <option 
                          key={index} 
                          value={sigla}
                        >
                          {/* {sigla} */}
                          {`${sigla} - ${nome}`}
                        </option>
                      );
                    })}
                  </select>
                ) : (
                  <input
                    type="text"
                    className="form-control"
                    id="unidade"
                    name="unidade"
                    value={formData.unidade}
                    onChange={handleChange}
                    placeholder="Digite a unidade de medida"
                    required
                  />
                )}
                {!isLoadingUnidades && unidadesMedida.length === 0 && (
                  <small className="text-muted">
                    Nenhuma unidade cadastrada para este item. Digite manualmente.
                  </small>
                )}
              </div>

              <div className="col-md-12 mb-3">
                <label htmlFor="descricaoComplementar" className="form-label">
                  <i className="bi bi-file-text me-1"></i>
                  Descrição Complementar
                </label>
                <textarea
                  className="form-control"
                  id="descricaoComplementar"
                  name="descricaoComplementar"
                  value={formData.descricaoComplementar}
                  onChange={handleChange}
                  placeholder="Digite informações adicionais sobre o item (opcional)"
                  rows="3"
                />
              </div>
            </div>
          </div>
          
          <div className="modal-footer">
            <button 
              type="button" 
              className="btn btn-secondary" 
              onClick={onClose}
            >
              <i className="bi bi-x-circle me-1"></i>
              Cancelar
            </button>
            <button 
              type="button" 
              className="btn btn-success" 
              onClick={handleSubmit}
            >
              <i className="bi bi-plus-circle me-1"></i>
              Adicionar Item
            </button>
          </div>
          </div>
        </div>
      </div>
      <div style={{ zIndex: 1060 }}>
        <DefaultModal {...setDefaultModal} />
      </div>
    </>
  );
}

export default ItemEditModal;
